import type { ForceCheckSymbolResult } from '@/lib/chin-bot-engine';
import { cn } from './types';

function BoxLevelRow({
  label,
  value,
  distancePips,
  digits,
  xPips,
  isNearest,
}: {
  label: string;
  value: number;
  distancePips: number | null;
  digits: number;
  xPips: number;
  isNearest: boolean;
}) {
  const triggered = distancePips !== null && distancePips <= xPips;
  return (
    <div className={cn('flex items-center justify-between gap-1.5', isNearest && 'font-semibold')}>
      <span className="text-foreground-400">{label}</span>
      <span className="font-mono text-foreground-700">{value.toFixed(digits)}</span>
      <span className={cn(
        'font-mono text-[9px] w-10 text-right',
        triggered ? 'text-amber-400 font-semibold' : 'text-foreground-400'
      )}>
        {distancePips !== null ? `${distancePips.toFixed(1)}p` : '—'}
      </span>
    </div>
  );
}

interface ChinForceCheckPanelProps {
  forceCheckRunning: boolean;
  forceCheckResults: ForceCheckSymbolResult[] | null;
  isLoading: boolean;
  onClose: () => void;
  onReRun: () => void;
}

export default function ChinForceCheckPanel({
  forceCheckRunning,
  forceCheckResults,
  isLoading,
  onClose,
  onReRun,
}: ChinForceCheckPanelProps) {
  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between bg-background-100">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-accent-500/10 flex items-center justify-center">
            {forceCheckRunning ? (
              <div className="w-3 h-3 border-2 border-accent-400 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <i className="ri-check-double-line text-accent-400 text-sm"></i>
            )}
          </div>
          <div>
            <h4 className="text-xs font-heading font-semibold text-foreground-900">Force Check Results</h4>
            {forceCheckResults && (
              <p className="text-[10px] text-foreground-500">
                {forceCheckResults.length} symbols checked · {
                  new Date(forceCheckResults[0]?.checkedAt || Date.now()).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                }
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-all cursor-pointer"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
      </div>

      {/* Loading */}
      {forceCheckRunning && (
        <div className="px-4 py-10 text-center">
          <div className="w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-xs text-foreground-500">
            {isLoading
              ? 'Refreshing historical candles from cTrader server...'
              : 'Running TMA50 cross & MA convergence checks on all symbols...'}
          </p>
        </div>
      )}

      {/* Results */}
      {!forceCheckRunning && forceCheckResults && forceCheckResults.length > 0 && (
        <>
          {/* Summary */}
          {(() => {
            const firedTma50 = forceCheckResults.filter((r) => r.tma50Cross.detected).length;
            const firedSmaTma = forceCheckResults.filter((r) => r.maConvergence.smaTma.detected).length;
            const firedAllMa = forceCheckResults.filter((r) => r.maConvergence.allMa.detected).length;
            const firedBoxVsTma50 = forceCheckResults.filter((r) => r.boxVsTma50.triggered).length;
            const anyFired = firedTma50 > 0 || firedSmaTma > 0 || firedAllMa > 0 || firedBoxVsTma50 > 0;
            return (
              <div className={cn(
                'px-4 py-2.5 border-b border-background-200/50 flex items-center gap-2 flex-wrap',
                anyFired ? 'bg-amber-500/5' : 'bg-emerald-500/5'
              )}>
                <span className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                  anyFired ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'
                )}>
                  {anyFired ? '!' : '✓'}
                </span>
                <span className="text-[11px] font-medium text-foreground-800">
                  {anyFired
                    ? `${firedTma50 + firedSmaTma + firedAllMa + firedBoxVsTma50} detection(s) fired across ${forceCheckResults.length} symbols`
                    : `All ${forceCheckResults.length} symbols checked — no detections`
                  }
                </span>
                <span className="text-[10px] text-foreground-400">
                  TMA50 Cross: {firedTma50} · SMA↔TMA: {firedSmaTma} · All 6 MA: {firedAllMa} · Box vs TMA50: {firedBoxVsTma50}
                </span>
              </div>
            );
          })()}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[100px]">Symbol</th>
                  <th className="text-center px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[80px]">Bid</th>
                  <th className="text-center px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[120px]">TMA50 Cross</th>
                  <th className="text-center px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[120px]">SMA↔TMA (Tụ)</th>
                  <th className="text-center px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[120px]">All 6 MA (Tụ)</th>
                  <th className="text-left px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[150px]">Box (Min/Max)</th>
                  <th className="text-left px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide w-[190px]">Box vs TMA50</th>
                  <th className="text-left px-3 py-2.5 text-foreground-500 font-semibold text-[10px] uppercase tracking-wide">Detail</th>
                </tr>
              </thead>
              <tbody>
                {forceCheckResults.map((r, idx) => {
                  const tma50Fired = r.tma50Cross.detected;
                  const smaTmaFired = r.maConvergence.smaTma.detected;
                  const allMaFired = r.maConvergence.allMa.detected;
                  const boxVsTma50Fired = r.boxVsTma50.triggered;

                  // Determine row highlight
                  const rowHighlight = tma50Fired || smaTmaFired || allMaFired || boxVsTma50Fired;

                  return (
                    <tr
                      key={r.symbolId}
                      className={cn(
                        'border-b border-background-200/30 transition-colors',
                        idx % 2 === 0 ? 'bg-background-50' : 'bg-background-50/50',
                        rowHighlight ? 'bg-amber-500/5 hover:bg-amber-500/10' : 'hover:bg-background-100/70'
                      )}
                    >
                      {/* Symbol */}
                      <td className="px-3 py-2.5">
                        <span className="font-mono font-semibold text-foreground-800 text-xs">{r.symbolName}</span>
                      </td>

                      {/* Bid */}
                      <td className="px-3 py-2.5 text-center font-mono text-foreground-600">
                        {r.currentBid !== null ? r.currentBid.toFixed(r.digits) : '—'}
                      </td>

                      {/* TMA50 Cross */}
                      <td className="px-3 py-2.5 text-center">
                        {r.bufferSize < 50 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title="Need at least 50 historical candles">Need 50+ candles (have {r.bufferSize})</span>
                        ) : r.bufferSize < 100 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title={`Buffer has ${r.bufferSize} candles. TMA50 needs ~100 candles to calculate (it is SMA of SMA).`}>Need ~100 candles (have {r.bufferSize})</span>
                        ) : r.tma50Cross.a0Tma50 === null ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title="Buffer has 100+ candles but TMA50 is still null — indicators may still be warming up">Calculating...</span>
                        ) : (
                          <span className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                            tma50Fired
                              ? r.tma50Cross.direction === 'ABOVE'
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'bg-red-500/10 text-red-400'
                              : 'bg-background-200/70 text-foreground-400'
                          )}>
                            {tma50Fired ? (
                              <>
                                <i className={cn(
                                  'text-[9px]',
                                  r.tma50Cross.direction === 'ABOVE' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'
                                )}></i>
                                {r.tma50Cross.direction}
                              </>
                            ) : (
                              '—'
                            )}
                          </span>
                        )}
                      </td>

                      {/* SMA↔TMA */}
                      <td className="px-3 py-2.5 text-center">
                        {r.bufferSize < 50 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400">Need 50+ candles (have {r.bufferSize})</span>
                        ) : r.bufferSize < 100 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title={`Buffer has ${r.bufferSize} candles. SMA50+TMA50 need ~100 candles.`}>Need ~100 candles (have {r.bufferSize})</span>
                        ) : r.maConvergence.smaTma.sma50 === null || r.maConvergence.smaTma.tma50 === null ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title="Buffer has 100+ candles but SMA50/TMA50 still null — indicators may still be warming up">Calculating...</span>
                        ) : r.maConvergence.smaTma.distancePips !== null ? (
                          <span className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                            smaTmaFired
                              ? 'bg-accent-500/10 text-accent-400'
                              : 'bg-background-200/70 text-foreground-400'
                          )}>
                            {smaTmaFired ? 'TỤ' : 'TÁCH'}
                            <span className="opacity-70 font-mono">
                              {r.maConvergence.smaTma.distancePips!.toFixed(1)}p
                            </span>
                          </span>
                        ) : (
                          <span className="text-foreground-400 text-[10px]">No data</span>
                        )}
                      </td>

                      {/* All 6 MA */}
                      <td className="px-3 py-2.5 text-center">
                        {r.bufferSize < 50 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400">Need 50+ candles (have {r.bufferSize})</span>
                        ) : r.bufferSize < 100 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title={`Buffer has ${r.bufferSize} candles. All 6 MAs need ~100 candles (especially HMA100 & TMA50).`}>Need ~100 candles (have {r.bufferSize})</span>
                        ) : r.maConvergence.allMa.availableCount < 6 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title={`${r.maConvergence.allMa.availableCount}/6 MA values ready`}>
                            {r.maConvergence.allMa.availableCount}/6 MA
                          </span>
                        ) : r.maConvergence.allMa.spreadPips !== null ? (
                          <span className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                            allMaFired
                              ? 'bg-accent-500/10 text-accent-400'
                              : 'bg-background-200/70 text-foreground-400'
                          )}>
                            {allMaFired ? 'TỤ' : 'TÁCH'}
                            <span className="opacity-70 font-mono">
                              {r.maConvergence.allMa.spreadPips!.toFixed(1)}p
                            </span>
                          </span>
                        ) : (
                          <span className="text-foreground-400 text-[10px]">No data</span>
                        )}
                      </td>

                      {/* Box (Min/Max) */}
                      <td className="px-3 py-2.5 align-top">
                        {r.box.available ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[9px] text-foreground-400 font-medium">x={r.box.xPips}p</span>
                              {r.box.triggered ? (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-500/10 text-amber-400">
                                  <i className="ri-alert-line text-[8px]"></i>NEAR
                                </span>
                              ) : (
                                <span className="text-[9px] text-foreground-300">—</span>
                              )}
                            </div>
                            <BoxLevelRow label="Min100" value={r.box.min100!} distancePips={r.box.distancePips.min100} digits={r.digits} xPips={r.box.xPips} isNearest={r.box.nearest?.label === 'Min100'} />
                            <BoxLevelRow label="Max100" value={r.box.max100!} distancePips={r.box.distancePips.max100} digits={r.digits} xPips={r.box.xPips} isNearest={r.box.nearest?.label === 'Max100'} />
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400">
                            Need 100+ candles (have {r.bufferSize})
                          </span>
                        )}
                      </td>

                      {/* Box vs TMA50 */}
                      <td className="px-3 py-2.5 align-top">
                        {r.boxVsTma50.available && r.boxVsTma50.pctToMin !== null && r.boxVsTma50.pctToMax !== null && r.boxVsTma50.tma50 !== null ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-1.5 mb-0.5">
                              <span className="text-[9px] text-foreground-400 font-medium">TMA50</span>
                              <span className="font-mono font-semibold text-foreground-800 text-[9px]">{r.boxVsTma50.tma50.toFixed(r.digits)}</span>
                              {boxVsTma50Fired ? (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-500/10 text-amber-400">
                                  <i className="ri-alert-line text-[8px]"></i>NEAR
                                </span>
                              ) : (
                                <span className="text-[9px] text-foreground-300">—</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-1.5">
                              <span className="text-foreground-400 text-[9px]">Min100</span>
                              <span className="font-mono text-foreground-700 text-[9px]">{r.boxVsTma50.min100!.toFixed(r.digits)}</span>
                              <span className={cn(
                                'font-mono text-[9px] w-10 text-right',
                                r.boxVsTma50.pctToMin! <= 25 ? 'text-amber-400 font-semibold' : 'text-foreground-400'
                              )}>
                                {r.boxVsTma50.pctToMin!.toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-1.5">
                              <span className="text-foreground-400 text-[9px]">Max100</span>
                              <span className="font-mono text-foreground-700 text-[9px]">{r.boxVsTma50.max100!.toFixed(r.digits)}</span>
                              <span className={cn(
                                'font-mono text-[9px] w-10 text-right',
                                r.boxVsTma50.pctToMax! <= 25 ? 'text-amber-400 font-semibold' : 'text-foreground-400'
                              )}>
                                {r.boxVsTma50.pctToMax!.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        ) : r.boxVsTma50.available ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400" title="Buffer has 100+ candles but TMA50 is still null — indicators may still be warming up">
                            No TMA50
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200/70 text-foreground-400">
                            Need 100+ candles (have {r.bufferSize})
                          </span>
                        )}
                      </td>

                      {/* Detail */}
                      <td className="px-3 py-2.5">
                        <div className="text-[10px] text-foreground-500 leading-relaxed max-w-[400px]">
                          {tma50Fired && (
                            <div className={cn(
                              'mb-0.5',
                              r.tma50Cross.direction === 'ABOVE' ? 'text-emerald-400' : 'text-red-400'
                            )}>
                              <strong>TMA50:</strong> {r.tma50Cross.detail}
                            </div>
                          )}
                          {smaTmaFired && (
                            <div className="text-accent-400 mb-0.5">
                              <strong>SMA↔TMA:</strong> {r.maConvergence.smaTma.detail}
                            </div>
                          )}
                          {allMaFired && (
                            <div className="text-accent-400 mb-0.5">
                              <strong>All 6:</strong> {r.maConvergence.allMa.detail}
                            </div>
                          )}
                          {boxVsTma50Fired && r.boxVsTma50.nearest && (
                            <div className="text-amber-400 mb-0.5">
                              <strong>Box vs TMA50:</strong> TMA50 near {r.boxVsTma50.nearest.label} ({r.boxVsTma50.nearest.pct.toFixed(1)}% ≤ 25%)
                            </div>
                          )}
                          {!tma50Fired && !smaTmaFired && !allMaFired && !boxVsTma50Fired && (
                            <span className="text-foreground-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-background-200/70 bg-background-100 flex items-center justify-between">
            <span className="text-[10px] text-foreground-400">
              Buffer depth: {forceCheckResults.map((r) => r.bufferSize).join(' / ')} candles
            </span>
            <button
              type="button"
              onClick={onReRun}
              disabled={forceCheckRunning}
              className="px-3 py-1.5 bg-accent-500/10 hover:bg-accent-500/20 text-accent-400 rounded-lg text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-refresh-line mr-1"></i>
              Re-run
            </button>
          </div>
        </>
      )}

      {/* No results + not loading */}
      {!forceCheckRunning && (!forceCheckResults || forceCheckResults.length === 0) && (
        <div className="px-4 py-10 text-center">
          <i className="ri-emotion-sad-line text-foreground-300 text-2xl block mb-3"></i>
          <p className="text-xs text-foreground-500">No results. Try running the check again.</p>
        </div>
      )}
    </div>
  );
}